import { dateLocale } from "../i18n/dateLocale";

/**
 * The one stamp formatter of the Super Admin: a row's moment as the UI language's own date
 * and a 2-digit HH:MM clock. Each screen used to call `toLocaleString(dateLocale(lang))`,
 * which in fr-CA is the CLDR long time ("2026-10-08 20 h 05 min 21 s") and renders
 * "Invalid Date" for a value it cannot read.
 *
 *   formatDate("2026-10-09T01:05:21Z", "fr")      → "2026-10-08"
 *   formatTime("2026-10-09T01:05:21Z", "fr")      → "20 h 05"
 *   formatDateTime("2026-10-09T01:05:21Z", "es")  → "8/10/2026 20:05"
 *   formatDateTime(null, "en")                    → "—"
 *
 * The zone is the browser's, as everywhere in the product. A date-only value ("YYYY-MM-DD")
 * names a UTC day and is NOT formatted here: use `calendarDay.utcDay`, which pins the zone.
 */

export const EMPTY = "—";

const HH_MM = { hour: "2-digit", minute: "2-digit" };

/**
 * ISO-8601 string | Date | epoch-ms number → a valid Date, else null. Missing, unreadable and
 * foreign-typed input (objects, booleans) are all "no date".
 */
function toDate(value) {
  let d;
  if (value instanceof Date) d = value;
  else if (typeof value === "number") d = new Date(value);
  else if (typeof value === "string" && value !== "") d = new Date(value);
  else return null;
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "2026-10-08" (en, fr) / "8/10/2026" (es); `EMPTY` for a missing or unreadable value. */
export function formatDate(value, lang) {
  const d = toDate(value);
  return d ? d.toLocaleDateString(dateLocale(lang)) : EMPTY;
}

/** "08:05 p.m." (en) / "20 h 05" (fr) / "20:05" (es), never seconds; `EMPTY` when unreadable. */
export function formatTime(value, lang) {
  const d = toDate(value);
  return d ? d.toLocaleTimeString(dateLocale(lang), HH_MM) : EMPTY;
}

/** `${formatDate} ${formatTime}`; a single `EMPTY` (not two) when unreadable. */
export function formatDateTime(value, lang) {
  const d = toDate(value);
  return d ? `${formatDate(d, lang)} ${formatTime(d, lang)}` : EMPTY;
}
